import { useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import axiosClient from "../../../../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import { IDataDetalharTitulosReceber } from "../../../../../../../types/api/financeiro/datalhar-titulos-receber";
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, Grid, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import PaperDraggable from "../../../../../../../components/PaperDraggable";
import SelectPlanoContas from "../../../../../../../components/SelectsSimpleApiQuery/SelectPlanoContas";
import { ContaContabilResumida } from "../../../../../../../types/api/geral/pesquisar-plano-contas";
import SelectLoja, { TObjValueLoja } from "../../../../../../../components/SelectsSimpleApiQuery/SelectLoja";
import { IDataCentroCustos } from "../../../../../../../types/api/contabilidade/pesquisar-centro-custos";
import SelectCentroCusto from "../../../../../../../components/SelectsSimpleApiQuery/SelectCentroCusto";
import { usePermission } from "../../../../../../../components/PermissionGuard";

export default function DialogEdit({
    open,
    handleClose,
    titulo,
    codigo
}: DialogEditProps) {

    const queryClient = useQueryClient();

    const [internFilters, setInternFilters] = useState<TInternFilters>({
        loja: titulo.loja ? { id: titulo.loja.id, nome: titulo.loja.nome } : null,
        comprador: titulo.comprador,
        grupo: titulo.grupo_recebimento,
        bordero: titulo.bordero,
        nsu_cartao:titulo.nsu_cartao,
        autorizacao_cartao: titulo.autorizacao_cartao,
        boleto: titulo.boleto,
        liberaRemFatAnt: titulo.liberado_remessa_faturamento_antecipado === "S" ? "Sim" : "Não",
        tipoDC: titulo.tipo_debito_credito === "D" ? "Débito" : "Crédito",
        centro_custo_fiscal: titulo.centro_custo_fiscal ? {
            id: titulo.centro_custo_fiscal.id,
            nome: titulo.centro_custo_fiscal.nome
        } : null,
        conta_contabil: titulo.conta_contabil ? {
            codigo_contabil: titulo.conta_contabil.codigo_contabil,
            nome: titulo.conta_contabil.nome,
            centro_custo: titulo.conta_contabil_custo ? {
                id: titulo.conta_contabil_custo.id || 0,
                nome: titulo.conta_contabil_custo.nome || ""
            } : null,
        } : null,
        contra_partida: titulo.contra_partida ? {
            codigo_contabil: titulo.contra_partida.codigo_contabil,
            nome: titulo.contra_partida.nome,
            centro_custo: titulo.contra_partida_custo ? {
                id: titulo.contra_partida_custo.id,
                nome: titulo.contra_partida_custo.nome
            } : null,
        } : null,
    })

    //valores importantes
    const permiteEditarCartao = usePermission('financeiro.contas-receber-editar-cartao');

    //querys e mutations
    const { mutate: atualizarTitulo, isLoading } = useMutation({
        mutationKey: 'atualizar-titulo-receber',
        mutationFn: (input: any) => axiosClient.put(`financeiro/contas-receber/atualizar-titulo-receber`, input),
        onError: (error) => {
            console.error(error);
            enqueueSnackbar(`Erro ao atualizar título`, { variant: "error" })
        },
        onSuccess: () => {
            queryClient.invalidateQueries("financeiro/contas-receber/detalhar-titulo-receber")
            queryClient.invalidateQueries("table-data-grid-financeiro/contas-receber/pesquisar-titulos-receber")
            enqueueSnackbar(`Título atualizado com sucesso!`, { variant: "success" })
            handleClose()
        }
    });
    //

    //funçoes manipuladoras
    const handleChangeInputs = (prop: keyof TInternFilters, value: any) => {
        setInternFilters(prevState => ({ ...prevState, [prop]: value }))
    }

    const handleSubmit = () => {
        const {
            loja,
            comprador,
            grupo,
            bordero,
            boleto,
            liberaRemFatAnt,
            tipoDC,
            conta_contabil,
            contra_partida,
            centro_custo_fiscal,
            nsu_cartao,
            autorizacao_cartao
        } = internFilters

        const params = {
            id_titulo: codigo,
            comprador,
            grupo,
            bordero,
            boleto,
            libera_rem_fat_ant: liberaRemFatAnt,
            tipo_dc: tipoDC.charAt(0),
            ...(loja ? { loja_id: loja.id } : {}),
            ...(centro_custo_fiscal ? { centro_custo_fiscal: centro_custo_fiscal.id } : {}),
            ...(conta_contabil ? {
                conta_contabil_cod: Number(conta_contabil.codigo_contabil),
                conta_contabil_centro_custo: Number(conta_contabil.centro_custo || 0)
            } : {}),
            ...(contra_partida ? {
                contra_partida_cod: Number(contra_partida.codigo_contabil),
                contra_partida_centro_custo: Number(contra_partida.centro_custo || 0)
            } : {}),
            nsu_cartao,
            autorizacao_cartao
        }
        atualizarTitulo(params)
    }
    //


    return (
        <Dialog
            open={open}
            onClose={handleClose}
            PaperComponent={PaperDraggable}
            aria-labelledby="draggable-dialog-title"
            fullWidth
            maxWidth={"md"}
            scroll="body"
        >
            <DialogTitle style={{ cursor: 'move', paddingBottom: 0 }} id="draggable-dialog-title">Editar título</DialogTitle>

            <DialogContent>
                <Grid container spacing={2} pt={3}>
                    <Grid size={{xs: 12, md: 6}}>
                        <SelectLoja
                            value={internFilters.loja}
                            onChange={(value) => handleChangeInputs("loja", value)}
                        />
                    </Grid>

                    <Grid size={{xs: 12, md: 6}}>
                        <TextField
                            fullWidth
                            label="Comprador"
                            variant="outlined"
                            value={internFilters.comprador}
                            onChange={({ target }) => handleChangeInputs("comprador", target.value)}
                        />
                    </Grid>

                    <Grid size={{xs: 12, md: 6}}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Grupo"
                            variant="outlined"
                            value={internFilters.grupo}
                            onChange={({ target }) => handleChangeInputs("grupo", target.value)}
                        />
                    </Grid>

                    <Grid size={{xs: 12, md: 6}}>
                        <TextField
                            fullWidth
                            label="Borderô"
                            variant="outlined"
                            value={internFilters.bordero}
                            onChange={({ target }) => handleChangeInputs("bordero", target.value)}
                        />
                    </Grid>

                    <Grid size={{xs: 12, md: 6}}>
                        <FormControl fullWidth>
                            <InputLabel id="select-possui-boleto">Boleto</InputLabel>
                            <Select
                                label="Boleto"
                                labelId="select-possui-boleto"
                                value={internFilters.boleto}
                                onChange={({ target }) => handleChangeInputs("boleto", target.value)}
                            >
                                {[
                                    "Sem boleto",
                                    "Emitido - sem registro",
                                    "Cobrança eletrônica reg.",
                                    "Emitido pelo ERP",
                                    "Emitido pela web",
                                ].map((option) => (
                                    <MenuItem value={option}>{option}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid size={{xs: 12, md: 6}}>
                        <FormControl fullWidth>
                            <InputLabel id="select-tipo-dc">Tipo D/C</InputLabel>
                            <Select
                                label="Tipo D/C"
                                labelId="select-tipo-dc"
                                value={internFilters.tipoDC}
                                onChange={({ target }) => handleChangeInputs("tipoDC", target.value)}
                            >
                                {["Débito", "Crédito"].map((option) => (
                                    <MenuItem value={option}>{option}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid size={{xs: 12, md: 6}}>
                        <FormControl fullWidth>
                            <InputLabel id="select-libera-rem">Libera Rem. FAT-ANT</InputLabel>
                            <Select
                                label="Libera Rem. FAT-ANT"
                                labelId="select-libera-rem"
                                value={internFilters.liberaRemFatAnt}
                                onChange={({ target }) => handleChangeInputs("liberaRemFatAnt", target.value)}
                            >
                                {["Sim", "Não"].map((option) => (
                                    <MenuItem value={option}>{option}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid size={{xs: 12, md: 6}}>
                        <SelectCentroCusto
                            value={internFilters.centro_custo_fiscal}
                            onChange={(value) => handleChangeInputs("centro_custo_fiscal", value)}
                        />
                    </Grid>

                    {
                        permiteEditarCartao && (
                            <>
                                <Grid size={{xs: 12, md: 6}}>
                                    <TextField
                                        fullWidth
                                        label="NSU"
                                        variant="outlined"
                                        type="number"
                                        value={internFilters.nsu_cartao}
                                        onChange={({ target }) => handleChangeInputs("nsu_cartao", target.value)}
                                    />
                                </Grid>
                                <Grid size={{xs: 12, md: 6}}>
                                    <TextField
                                        fullWidth
                                        label="Autorização cartão"
                                        variant="outlined"
                                        value={internFilters.autorizacao_cartao}
                                        onChange={({ target }) => handleChangeInputs("autorizacao_cartao", target.value)}
                                    />
                                </Grid>
                            </>
                        )
                    }

                    <Grid size={{xs: 12}}><Divider /></Grid>

                    <Grid size={{xs: 12, md: 6}}>
                        <SelectPlanoContas
                            inputLabel="Conta contábil"
                            value={internFilters.conta_contabil}
                            onChangeValue={(value) => handleChangeInputs("conta_contabil", value)}
                        />
                    </Grid>

                    <Grid size={{xs: 12, md: 6}}>
                        <SelectPlanoContas
                            inputLabel="Contra partida"
                            value={internFilters.contra_partida}
                            onChangeValue={(value) => handleChangeInputs("contra_partida", value)}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleClose}
                >
                    Cancelar
                </Button>

                <Button
                    disabled={isLoading}
                    variant="contained"
                    onClick={() => handleSubmit()}
                >
                    {(isLoading) && <CircularProgress size={20} sx={{ marginRight: 1 }} />}
                    Editar
                </Button>
            </DialogActions>
        </Dialog>
    )
}

interface DialogEditProps {
    open: boolean;
    handleClose: () => void;
    titulo: IDataDetalharTitulosReceber
    codigo: string
}

type TInternFilters = {
    loja: TObjValueLoja | null,
    comprador: string,
    grupo: number | null,
    bordero: string,
    nsu_cartao: number;
    autorizacao_cartao: string;
    boleto: string,
    liberaRemFatAnt: "Sim" | "Não",
    tipoDC: "Débito" | "Crédito",
    centro_custo_fiscal: IDataCentroCustos | null,
    conta_contabil: ContaContabilResumida | null,
    contra_partida: ContaContabilResumida | null,
}